import { useMemo } from "react";
import { useDispatch } from "react-redux";

import { useDigitalSizerClient } from "./useDigitalSizerApi";
import { cachedDataItemClear } from "../redux/actions/cachedDataActions";
import { CACHED_DATA_REDUCER_TYPES } from "../redux/reducers/cachedDataReducer";

// Org RBAC groups on eastview-os's /groups router, via the LOS client (dsHttp →
// eastview-los). Groups are membership buckets (Loan Officers, US Underwriters,
// Closers, …) that carry business-unit / departments / related-roles / portal-
// activities metadata and a resolved member list (linked to all_users by _id
// where matched). Mutations are admin-only on the backend (it 403s otherwise);
// the screen surfaces those errors. See app/routers/groups.py.
export const useGroupsApi = () => {
  const client = useDigitalSizerClient();
  const dispatch = useDispatch();

  return useMemo(() => {
    /**
     * Membership changes gate real UI (the Users screen's Activity tab and its
     * header menu entry both key off the logged-in user's groups), so every
     * mutation drops the cached list here rather than at the call sites. Doing
     * it in one place is what makes "remove yourself from a group and watch the
     * tab go" work without a refresh — and keeps a future call site from
     * forgetting.
     */
    const invalidateUserGroups = () =>
      dispatch(cachedDataItemClear(CACHED_DATA_REDUCER_TYPES.USER_GROUPS));

    const get = async (p) => (await client.get(p)).data;
    const post = async (p, b) => (await client.post(p, b)).data;
    const put = async (p, b) => (await client.put(p, b)).data;
    const del = async (p) => (await client.delete(p)).data;

    return {
      // GET /groups → array of OrgGroup docs (name, metadata, members[], memberCount).
      listGroups: () => get("/groups"),
      // GET /groups/:id → one group (id may be the ObjectId or the unique name).
      getGroup: (groupId) => get(`/groups/${groupId}`),
      // POST /groups → create. Body: { name (required), description, businessUnit,
      // departments[], relatedRoles[], portalActivities, relatedVendors }. Admin only.
      createGroup: (body) => post("/groups", body),
      // PUT /groups/:id → update metadata only (membership has its own endpoints).
      // Allowed keys: description, businessUnit, departments, relatedRoles,
      // portalActivities, relatedVendors. Admin only.
      updateGroup: (groupId, body) => put(`/groups/${groupId}`, body),
      // POST /groups/:id/members → add member. Body: { email (required), fullName? }.
      // The backend resolves the email to a known user (primary OR email_aliases)
      // and records matched/userId. Admin only.
      addMember: async (groupId, email, fullName) => {
        const res = await post(`/groups/${groupId}/members`, {
          email,
          fullName,
        });
        invalidateUserGroups();
        return res;
      },
      // DELETE /groups/:id/members/:email → remove a member (case-insensitive). Admin only.
      removeMember: async (groupId, email) => {
        const res = await del(
          `/groups/${groupId}/members/${encodeURIComponent(email)}`,
        );
        invalidateUserGroups();
        return res;
      },
      // GET /groups/for-user/:email → groups a given user belongs to.
      groupsForUser: (email) =>
        get(`/groups/for-user/${encodeURIComponent(email)}`),
    };
  }, [client, dispatch]);
};

export default useGroupsApi;
